import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  USER_TYPES,
  USER_TYPE_NAMES,
} from "../store/actions/userManagement";
import Loading from "../components/Loading";
import { toast } from "react-toastify";
import { formatDisplayUserId } from "../utils/userIdFormat";
import { useGetUsersByTypeQuery, useUpdateUserBalanceMutation } from "../services/auth/authApi";

// Search Icon Component
const SearchIcon = () => (
  <svg
    viewBox="64 64 896 896"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M909.6 854.5L649.9 594.8C690.2 542.7 712 479 712 412c0-80.2-31.3-155.4-87.9-212.1-56.6-56.7-132-87.9-212.1-87.9s-155.5 31.3-212.1 87.9C143.2 256.5 112 331.8 112 412c0 80.1 31.3 155.5 87.9 212.1C256.5 680.8 331.8 712 412 712c67 0 130.6-21.8 182.7-62l259.7 259.6a8.2 8.2 0 0011.6 0l43.6-43.5a8.2 8.2 0 000-11.6zM570.4 570.4C528 612.7 471.8 636 412 636s-116-23.3-158.4-65.6C211.3 528 188 471.8 188 412s23.3-116.1 65.6-158.4C296 211.3 352.2 188 412 188s116.1 23.2 158.4 65.6S636 352.2 636 412s-23.3 116.1-65.6 158.4z"></path>
  </svg>
);

// Left Arrow Icon
const LeftArrowIcon = () => (
  <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
    <path d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 000 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z"></path>
  </svg>
);

// Right Arrow Icon
const RightArrowIcon = () => (
  <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
    <path d="M765.7 486.8L314.9 134.7A7.97 7.97 0 00302 141v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.9 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1a31.96 31.96 0 000-50.4z"></path>
  </svg>
);

const UpdateLimitPage = () => {
  const navigate = useNavigate();
  const { userType: userTypeParam } = useParams();

  // Map URL param to user type
  const userTypeMap = {
    superadmin: USER_TYPES.SUPERADMIN,
    admin: USER_TYPES.ADMIN,
    miniadmin: USER_TYPES.MINIADMIN,
    "mini-admin": USER_TYPES.MINIADMIN,
    master: USER_TYPES.MASTER,
    supermaster: USER_TYPES.SUPERMASTER,
    "super-master": USER_TYPES.SUPERMASTER,
    agent: USER_TYPES.AGENT,
    client: USER_TYPES.CLIENT,
  };

  const userType =
    userTypeMap[userTypeParam?.toLowerCase()] || USER_TYPES.MINIADMIN;
  const userTypeName = USER_TYPE_NAMES[userType] || "User";
  const formatUserId = function(userId) {
    return formatDisplayUserId(userId);
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Get data from RTK Query API
  const { data: usersData, isLoading: loading, error: errors, refetch } = useGetUsersByTypeQuery(
    { userType: userTypeName, search: '', page: currentPage, limit: itemsPerPage },
    { skip: !userTypeName }
  );

  const users = usersData?.data || [];
  const pagination = usersData?.pagination || {};

  // RTK Query mutation for updating balance
  const [updateBalance] = useUpdateUserBalanceMutation();

  // Local state
  const [limitAmounts, setLimitAmounts] = useState({});
  const [processing, setProcessing] = useState({});
  const [searchCode, setSearchCode] = useState("");
  const [searchName, setSearchName] = useState("");
  const [showCodeSearch, setShowCodeSearch] = useState(false);
  const [showNameSearch, setShowNameSearch] = useState(false);


  // Calculate chips: balance - balanceWithPnl
  const calculateChips = function (user) {
    const balance = user.balance || 0;
    const balanceWithPnl = user.balanceWithPnl || 0;
    return balance + balanceWithPnl;
  };

  // Handle amount input change
  const handleAmountChange = function (userId, value) {
    setLimitAmounts(function (prev) {
      return { ...prev, [userId]: value };
    });
  };

  // Handle Add Limit (deposit - type: "credit")
  const handleAddLimit = async function (user) {
    const amount = parseFloat(limitAmounts[user.userId]);
    if (!amount || isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setProcessing(function (prev) {
      return { ...prev, [user.userId]: "add" };
    });

    try {
      await updateBalance({
        userId: user.id,
        amount: amount,
        type: "credit"
      }).unwrap();

      toast.success(
        <span style={{ fontWeight: "bold", fontSize: "16px" }}>
          Limit added successfully!
        </span>,
      );
      setLimitAmounts(function (prev) {
        return { ...prev, [user.userId]: "" };
      });
      // Refresh user list to get updated balance
      refetch();
    } catch (error) {
      toast.error(
        <span style={{ fontWeight: "bold", fontSize: "16px" }}>
          {error?.data?.message || "Failed to add limit"}
        </span>,
      );
    }

    setProcessing(function (prev) {
      return { ...prev, [user.userId]: null };
    });
  };

  // Handle Minus Limit (withdraw - type: "debit")
  const handleMinusLimit = async function (user) {
    const amount = parseFloat(limitAmounts[user.userId]);
    if (!amount || isNaN(amount) || amount <= 0) {
      toast.error(
        <span style={{ fontWeight: "bold", fontSize: "16px" }}>
          Please enter a valid amount
        </span>,
      );
      return;
    }

    setProcessing(function (prev) {
      return { ...prev, [user.userId]: "minus" };
    });

    try {
      await updateBalance({
        userId: user.id,
        amount: amount,
        type: "debit"
      }).unwrap();

      toast.success(
        <span style={{ fontWeight: "bold", fontSize: "16px" }}>
          Limit deducted successfully!
        </span>,
      );
      setLimitAmounts(function (prev) {
        return { ...prev, [user.userId]: "" };
      });
      refetch();
    } catch (error) {
      toast.error(
        <span style={{ fontWeight: "bold", fontSize: "16px" }}>
          {error?.data?.message || "Failed to deduct limit"}
        </span>,
      );
    }

    setProcessing(function (prev) {
      return { ...prev, [user.userId]: null };
    });
  };

  // Filter users based on search
  const filteredUsers = users.filter(function (user) {
    const codeMatch =
      !searchCode ||
      user.userId.toLowerCase().includes(searchCode.toLowerCase());
    const nameMatch =
      !searchName ||
      (user.fullName && user.fullName.toLowerCase().includes(searchName.toLowerCase()));
    return codeMatch && nameMatch;
  });

  // Handle page change
  const handlePageChange = function (page) {
    setCurrentPage(page);
  };

  return (
    <div className="w-full">
      <div className="bg-white shadow rounded">
        {/* Header */}
        <div className="bg-gray-600 px-5 py-3 flex items-center justify-between">
          <span className="text-xl text-white font-normal capitalize">
            Update Limit
          </span>
          <button
            onClick={function () {
              navigate(-1);
            }}
            className="bg-blue-500 text-white px-4 py-1 rounded hover:bg-blue-600"
          >
            Back
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loading />
            </div>
          ) : errors ? (
            <div className="p-4 text-red-500 text-center">{errors?.message || 'Error loading users'}</div>
          ) : (
            <table className="w-full border-collapse text-sm border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  {/* Code Column with Search */}
                  <th className="p-2 border border-gray-300 text-left">
                    <div className="flex items-center justify-between">
                      <span>Code</span>
                      <button
                        onClick={function () {
                          setShowCodeSearch(!showCodeSearch);
                        }}
                        className="text-gray-500 hover:text-gray-700 ml-2"
                      >
                        <SearchIcon />
                      </button>
                    </div>
                    {showCodeSearch && (
                      <input
                        type="text"
                        value={searchCode}
                        onChange={function (e) {
                          setSearchCode(e.target.value);
                        }}
                        placeholder="Search code..."
                        className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm font-normal"
                        onClick={function (e) {
                          e.stopPropagation();
                        }}
                      />
                    )}
                  </th>
                  {/* Name Column with Search */}
                  <th className="p-2 border border-gray-300 text-left">
                    <div className="flex items-center justify-between">
                      <span>Name</span>
                      <button
                        onClick={function () {
                          setShowNameSearch(!showNameSearch);
                        }}
                        className="text-gray-500 hover:text-gray-700 ml-2"
                      >
                        <SearchIcon />
                      </button>
                    </div>
                    {showNameSearch && (
                      <input
                        type="text"
                        value={searchName}
                        onChange={function (e) {
                          setSearchName(e.target.value);
                        }}
                        placeholder="Search name..."
                        className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm font-normal"
                        onClick={function (e) {
                          e.stopPropagation();
                        }}
                      />
                    )}
                  </th>
                  <th className="p-2 border border-gray-300 text-left">
                    C.Chip
                  </th>
                  <th className="p-2 border border-gray-300 text-left">
                    Add / Minus Limit
                  </th>
                  <th className="p-2 border border-gray-300 text-left">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="p-8 text-center text-gray-500">
                      No {userTypeName.toLowerCase()}s found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map(function (user, index) {
                    const rowBgColor = index % 2 === 0 ? "var(--row-even)" : "var(--row-odd)";
                    const isProcessing = processing[user.userId];
                    // Calculate chips as balance - {balanceWithPnl}
                    const chips = calculateChips(user);

                    return (
                      <tr
                        key={user.userId}
                        style={{ backgroundColor: rowBgColor }}
                      >
                        <td className="p-2 border border-gray-300">
                          {formatUserId(user.userId)}
                        </td>
                        <td className="p-2 border border-gray-300">
                          {user.fullName}
                        </td>
                        <td className="p-2 border border-gray-300">
                          {chips.toFixed(2)}
                        </td>
                        <td className="p-2 border border-gray-300">
                          <input
                            type="text"
                            value={limitAmounts[user.userId] || ""}
                            onChange={function (e) {
                              handleAmountChange(user.userId, e.target.value);
                            }}
                            className="border border-slate-200 rounded-lg px-3 py-1.5 w-[110px] text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all"
                            placeholder="Amount"
                          />
                        </td>
                        <td className="p-2 border border-gray-300">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={function () { handleAddLimit(user); }}
                              disabled={!!isProcessing}
                              className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-all shadow-sm"
                            >
                              {isProcessing === "add" ? (
                                <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                                </svg>
                              ) : (
                                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3">
                                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                                </svg>
                              )}
                              Add
                            </button>
                            <button
                              onClick={function () { handleMinusLimit(user); }}
                              disabled={!!isProcessing}
                              className="inline-flex items-center gap-1.5 bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-all shadow-sm"
                            >
                              {isProcessing === "minus" ? (
                                <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                                </svg>
                              ) : (
                                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3">
                                  <path d="M19 13H5v-2h14v2z"/>
                                </svg>
                              )}
                              Minus
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination && (
          <div className="flex justify-between items-center p-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Items per page:</span>
              <select
                className="px-2 py-1 border rounded text-sm"
                value={itemsPerPage}
                onChange={function (e) {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-600">
                Total: {pagination.totalCount || usersData?.count || 0}
              </span>
            </div>
            <div className="flex gap-1">
              <button
                className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={currentPage === 1}
                onClick={function () {
                  handlePageChange(currentPage - 1);
                }}
              >
                <LeftArrowIcon />
              </button>
              <button className="px-3 py-1 bg-blue-500 text-white rounded">
                {currentPage} / {pagination.totalPages || Math.ceil((usersData?.count || 0) / itemsPerPage) || 1}
              </button>
              <button
                className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={currentPage >= (pagination.totalPages || Math.ceil((usersData?.count || 0) / itemsPerPage) || 1)}
                onClick={function () {
                  handlePageChange(currentPage + 1);
                }}
              >
                <RightArrowIcon />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateLimitPage;
